//
// TLS Dose Engine Database page
//
import { useState, useEffect } from "react";

import gSession from '../script/session.js';
import gUtilities from '../script/utilities';

import TabWidget from './TabWidget';
import TableWidget from './TableWidget';

const ClusterPage = () => {
    let emptyReport = { jobs: [] };
    const [report, setReport] = useState(emptyReport);
    const [jobDetails, setJobDetails] = useState(gUtilities.emptyTable());
    const [jobsTable, setJobsTable] = useState(gUtilities.emptyTable());
    const [flagsTable, setFlagsTable] = useState(gUtilities.emptyTable());
    const [rsvDetails, setRsvDetails] = useState(gUtilities.emptyTable());
    const [rsvTable, setRsvTable] = useState(gUtilities.emptyTable());
    const [selectedJob, setSelectedJob] = useState(null);
    const [selectedRsv, setSelectedRsv] = useState(null);
    const [queueTables, setQueueTables] = useState(null);
    const [message, setMessage] = useState("");

    async function updateReport() {
        let request = {
            service: "agent.report"
        };

        let response = await gSession.exchange(request);
        if (response.isError) {
            gUtilities.showError("Report", response.message);
        } else {
            updateRsvTable(response);
            updateJobsTable(response);
            updateQueueTables(response);
            setReport(response);
        }
    }

    function updateQueueTables(response) {
        let data = { queues: [] };
        let queues = {};
        let tmp;

        let table;
        for (let n of response.nodes) {
            if (queues.hasOwnProperty(n.queue)) {
                table = queues[n.queue];
            } else {
                table = gUtilities.emptyTable();
                table.header = ["Name", "State", "Time", "Text"];
                table.columns = ["name", "state", "time", "text"];
                queues[n.queue] = table;

                data.queues.push({
                    title: n.queue,
                    table: table
                });
            }

            table.rows.push([n.name, n.state, n.time, n.text]);
            tmp = n.time;
        }

        for (let q of data.queues) {
            let ml;
            let mr;
            let nl;
            let nr;

            q.table.rows.sort((l, r) => {
                ml = l[0].match(/-(\d+)$/);
                mr = r[0].match(/-(\d+)$/);

                nl = parseInt(ml[1]);
                nr = parseInt(mr[1]);

                if (l[1] !== r[1]) {
                    if (l[1] === "OFF") {
                        return 1;
                    } else if (r[1] === "OFF") {
                        return -1;
                    }
                }

                if (nl < nr) {
                    return -1;
                }

                return 1;
            });
        }

        setQueueTables(data);
    }

    function updateJobsTable(rpt) {
        let table = {
            header: [
                "JID", "Fingerprint", "State", "Reservations"
            ],
            columns: [
                "jid", "fingerprint", "state", "rids"
            ],
            rows: []
        }

        for (let job of rpt.jobs) {
            table.rows.push([job.jid, gUtilities.fingerprint(job.job_id), job.state, job.rids]);
        }

        setJobsTable(table);

        if (selectedJob) {
            updateJobDetails(rpt);
        }
    }

    function updateRsvTable(rpt) {
        let table = {
            header: [
                "RID", "Queue", "State", "Released", "SlurmID", "Tasks", "Spinners"
            ],
            columns: [
                "rid", "queue", "state", "released", "slurmid", "total_tasks", "spinners"
            ],
            rows: []
        }

        for (let rsv of rpt.reservations) {
            table.rows.push([rsv.rid, rsv.queue, rsv.state, rsv.released, rsv.fleet.slurmid, rsv.fleet.total_tasks, rsv.fleet.spinners]);
        }

        setRsvTable(table);

        if (selectedRsv) {
            updateRsvDetails(rpt);
        }
    }

    function updateRsvDetails(rpt) {
        let table = gUtilities.emptyTable();
        table.header.push("Node");
        table.header.push("State");
        table.columns.push("name");
        table.columns.push("state");

        let rsv = null;
        for (let entry of rpt.reservations) {
            if (entry.rid === selectedRsv) {
                rsv = entry;
                break;
            }
        }

        if (!rsv) {
            setRsvDetails(gUtilities.emptyTable());
            return;
        }

        let row;
        for (let node of rsv.fleet.nodes) {
            row = [node.name, node.state];
            table.rows.push(row);
        }

        setRsvDetails(table);
    }

    function collectFlags(item, id, order, entries) {
        let html;

        order.push(id);
        entries[id] = [];

        for (let pair of item.flags) {
            html = pair.value ? `<b>${pair.name.toUpperCase()}</b>` : pair.name.toLowerCase();
            entries[id].push(html);
        }
    }

    function generateFlagsTable(order, flags) {
        let nrows = 0;
        for (let id of order) {
            if (flags[id].length > nrows) {
                nrows = flags[id].length;
            }
        }

        let ft = gUtilities.emptyTable();

        for (let i = 0; i < nrows; i++) {
            ft.rows.push([]);
        }

        let entries;
        for (let id of order) {
            ft.header.push(id);
            ft.columns.push(id);

            entries = flags[id];
            for (let i = 0; i < nrows; i++) {
                if (i < entries.length) {
                    ft.rows[i].push(entries[i]);
                } else {
                    ft.rows[i].push("---");
                }
            }
        }

        return ft;
    }

    function updateJobDetails(rpt) {
        let job;
        for (let entry of rpt.jobs) {
            if (entry.jid === selectedJob) {
                job = entry;
                break;
            }
        }

        let flags = {};
        let order = [];

        collectFlags(job, job.jid, order, flags);

        let details = {
            header: [
                "Type", "ID", "Description", "State", "Duration", "Outcome", "Duration", "Details"
            ],
            columns: [
                "type", "id", "description", "state", "state_duration", "outcome", "total_duration", "details"
            ],
            rows: [
                ["Job", job.jid, gUtilities.fingerprint(job.job_id), job.state, job.state_duration, job.outcome, job.total_duration]
            ]
        }

        let cmp;
        let progress;
        let min_progress = 100;
        for (let beam of job.beams) {
            cmp = beam.computation;

            if (cmp.evaluation === "PENDING" || cmp.evaluation === "FAILURE") {
                progress = "0 %";
            } else if (cmp.evaluation === "SUCCESS") {
                progress = "100 %";
            } else {
                progress = `${cmp.progress} %`;
            }

            if (cmp.progress < min_progress) {
                min_progress = cmp.progress;
            }

            details.rows.push(["Beam", beam.bid, `beam-${beam.number}`, beam.state, beam.state_duration, beam.outcome, beam.total_duration, progress]);
            details.rows.push(["Computation", cmp.cid, cmp.queue, cmp.state, cmp.state_duration, cmp.outcome, cmp.total_duration, `Batch=${cmp.batch}`])

            collectFlags(beam, beam.bid, order, flags);
            collectFlags(cmp, cmp.cid, order, flags);
        }

        if (job.outcome === "SUCCESS") {
            progress = "100 %";
        } else if (job.outcome === "FAILURE" || job.outcome === "PENDING") {
            progress = "0 %";
        } else {
            progress = 10.0 + (0.8 * min_progress);
            progress = `${progress} %`;
        }

        details.rows[0].push(progress);

        let ftable = generateFlagsTable(order, flags);

        setFlagsTable(ftable);
        setJobDetails(details);
        setMessage(job.message);
    }

    function watchJob(data) {
        setSelectedJob(data.jid);
    }

    function watchRsv(data) {
        setSelectedRsv(data.rid);
    }

    function rowClass(row) {
        if (row && (row[0] === selectedJob || row[0] === selectedRsv)) {
            return "table-success";
        } else {
            return "table-info";
        }
    };

    let jobButtonData = {
        buttons: [
            {
                label: "Watch",
                callback: watchJob
            }
        ]
    };

    let rsvButtonData = {
        buttons: [
            {
                label: "Watch",
                callback: watchRsv
            }
        ]
    };

    function scheduleUpdate() {
        let id = setTimeout(() => {
            try {
                updateReport();
            } catch (ex) {
                console.log("ERROR: [EXCEPTION] Report - ", ex);
            }
        }, 1000);

        return id;
    }

    useEffect(() => {
        let timer_id = scheduleUpdate();

        // cleanup function: stop updates.
        return () => {
            clearTimeout(timer_id);
        };
    }, [report]);

    return (
        <div id="w_ClusterPageRootDiv">
            {queueTables &&
                <TabWidget>
                    <div title="Reservations">
                        <fieldset>
                            <legend>Reservations</legend>
                            <TableWidget data={rsvTable} buttonData={rsvButtonData} rowClassFunction={rowClass} />
                        </fieldset>
                        <fieldset>
                            <legend>Reservation Details</legend>
                            <TableWidget data={rsvDetails} />
                        </fieldset>
                    </div>
                    <div title="Jobs">
                        <fieldset>
                            <legend>Jobs</legend>
                            <TableWidget data={jobsTable} buttonData={jobButtonData} rowClassFunction={rowClass} />
                        </fieldset>
                        <fieldset>
                            <legend>Job Details</legend>
                            <TableWidget data={jobDetails} />
                        </fieldset>
                        <fieldset>
                            <legend>Flags</legend>
                            <TableWidget data={flagsTable} />
                        </fieldset>
                        <fieldset>
                            <legend>Message</legend>
                            <p>{message}</p>
                        </fieldset>
                    </div>
                    {
                        queueTables.queues.map(queue => (
                            <div title={queue.title}>
                                <TableWidget data={queue.table} />
                            </div>
                        ))
                    }
                </TabWidget>
            }
        </div>
    );
}

export default ClusterPage;